import type { NativeF0Frame } from '@muzly/shared';
import type { Clip, ClipSet } from '@muzly/voice-engine';

import { readItem, removeItem, writeItem } from '../../../core';
import { captureAttempts, framesToClips, sustainStats } from '../capture';
import { RECORD_TESTS } from '../recordTests';

import type {
  CaptureAttempts,
  CaptureSource,
  FrameStats,
  SegmentId,
} from '../capture';
import type { RecordPosition } from '../recordTests';

/**
 * diagnosisProgress — 진단 **도중**의 로컬 영속화. "2/3부터 이어서"의 유일한 소스.
 *
 * 완료 회차(diagnosisStore)와 **다른 키·다른 버전**을 쓴다: 저장 스키마와 진행 스키마는
 * 서로 독립으로 바뀐다(테스트 순서가 바뀌면 진행만 무효가 되고, 축이 늘면 회차만 무효가
 * 된다). 한 버전에 묶으면 한쪽 변경이 다른 쪽 데이터를 이유 없이 버린다.
 *
 * **프레임이 아니라 병합된 `Clip`을 남긴다.** 모바일 WebView는 `localStorage.setItem`을
 * 몽키패치해 값 전체를 postMessage로 네이티브에 미러링한다(diagnosisStore 주석). 20초
 * 세그먼트의 원시 프레임은 ~940개 객체(≈100KB JSON)이고 재시도마다 늘어난다 — 세그먼트를
 * 넘길 때마다 그게 RPC를 탄다. 병합 클립은 숫자 배열 몇 개(≈10KB)이고, 무엇보다
 * `buildReport`가 실제로 먹는 것이 그거다.
 *
 * 읽을 수 없는 진행은 **버리고 키까지 지운다** — 마이그레이션은 없다(diagnosisStore와 같은
 * 정책). 남겨두면 매 진입마다 같은 쓰레기를 다시 파싱하고, 화면은 계속 "이어서"를 권한다.
 *
 * localStorage 접근은 `core/localStore`가 방어적으로 감싼다(사생활 모드·용량 초과 대비).
 */

export const PROGRESS_KEY = 'muzly.diagnosis.progress';

/**
 * 진행 스키마가 바뀌면 올린다. 읽는 쪽은 모르는 버전을 **버린다**.
 *
 * 2 — 진단이 3개(`sustain`/`range`/`song`)에서 4개(`sustain`/`low`/`high`/`song`)로
 * 갈라졌다. `pos.testIndex`가 그 배열의 인덱스라 v1 진행의 index 2는 예전엔 노래였는데
 * 지금은 고음을 가리킨다 — 올리지 않으면 이어하기가 **다른 진단**에서 열린다.
 */
export const DIAGNOSIS_PROGRESS_VERSION = 2;

export interface DiagnosisProgress {
  version: number;
  /** 재개할 자리 — 이미 마친 세그먼트의 **다음**이다. */
  pos: RecordPosition;
  /** 마친 세그먼트들의 병합 클립. 아직 안 찍은 세그먼트는 빠져 있다. */
  clips: ClipSet;
  /** `/a/` 길게의 파생 스칼라. 안 실어 온 값은 필드가 없다(0 아님). */
  stats: FrameStats;
  /** 이 진행을 만든 전송. 재개 때 바뀌면 회차가 native로 안 남는다(resumedSource). */
  source: CaptureSource;
  updatedAt: number;
}

/**
 * 지금 서 있는 단계(1-기준) — `pos.testIndex`는 **다음에 잴** 진단이라 그 index + 1이다.
 * 홈의 이어하기 팝업이 `n/4 단계 진행 중`으로 읽는다. 분모(진단 수)를 넘지 않는다.
 */
export const resumeStep = (progress: DiagnosisProgress): number =>
  Math.min(progress.pos.testIndex + 1, RECORD_TESTS.length);

export const clearProgress = (): void => removeItem(PROGRESS_KEY);

export const saveProgress = (progress: DiagnosisProgress): void =>
  writeItem(PROGRESS_KEY, JSON.stringify(progress));

const isFiniteNumber = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v);

const isNumberArray = (v: unknown): v is number[] =>
  Array.isArray(v) && v.every(isFiniteNumber);

/**
 * 클립 모양 검사. `buildReport`가 곧바로 먹는 값이라 배열 하나가 문자열이어도 리포트가
 * NaN으로 번진다 — 여기서 막지 않으면 그 NaN이 회차로 저장된다.
 */
const isClip = (v: unknown): v is Clip => {
  const clip = v as Clip | undefined;
  return (
    clip != null &&
    typeof clip === 'object' &&
    isNumberArray(clip.pitches) &&
    isNumberArray(clip.hnrs) &&
    isNumberArray(clip.centroids) &&
    isFiniteNumber(clip.voicedSec)
  );
};

/**
 * 위치가 지금 테스트 구성 안에 있나. 테스트가 재편되면(슬라이스 05가 노래를 더한다)
 * 예전 위치가 없는 인덱스를 가리킬 수 있고, 그대로 `testAt`에 넣으면 크래시다.
 */
const isPosition = (v: unknown): v is RecordPosition => {
  const pos = v as RecordPosition | undefined;
  if (
    pos == null ||
    !Number.isInteger(pos.testIndex) ||
    !Number.isInteger(pos.segIndex)
  ) {
    return false;
  }
  const test = RECORD_TESTS[pos.testIndex];
  return (
    test != null && pos.segIndex >= 0 && pos.segIndex < test.segments.length
  );
};

const isCaptureSource = (v: unknown): v is CaptureSource =>
  v === 'native' || v === 'mic' || v === 'mock';

const isProgress = (v: unknown): v is DiagnosisProgress => {
  const p = v as DiagnosisProgress | undefined;
  return (
    p != null &&
    typeof p === 'object' &&
    !Array.isArray(p) &&
    p.version === DIAGNOSIS_PROGRESS_VERSION &&
    isPosition(p.pos) &&
    p.clips != null &&
    typeof p.clips === 'object' &&
    isClip(p.clips.sustain) &&
    isCaptureSource(p.source)
  );
};

/** 재개할 진행. 읽을 수 없으면 null을 내고 남은 값도 지운다. */
export const loadProgress = (): DiagnosisProgress | null => {
  const raw = readItem(PROGRESS_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isProgress(parsed)) {
      return parsed;
    }
  } catch {
    /* fall through — 파싱 실패도 "읽을 수 없음"이다 */
  }
  clearProgress();
  return null;
};

/** 세그먼트 id → 그 세그먼트의 시도들. `useRecordMachine`의 `Attempts`와 같은 모양. */
type Attempts = Partial<Record<SegmentId, NativeF0Frame[][]>>;

export interface CapturedDiagnosis {
  clips: ClipSet;
  stats: FrameStats;
}

/**
 * 복원본 + 이번 세션 프레임 → `buildReport`에 넣을 한 벌.
 *
 * **이번 세션에 다시 찍은 세그먼트가 이긴다.** `framesToClips`는 `sustain`이 비어도 빈
 * 클립을 내주므로(타입상 필수라서) 그대로 쓰면 재개 시 복원한 지속음을 빈 클립이 덮는다 —
 * 세그먼트마다 "이번에 찍었나"를 먼저 묻는 이유다.
 */
export const mergeCaptured = (
  restored: DiagnosisProgress | null,
  attempts: Attempts,
  /**
   * 지속음 파생 스칼라를 내는 함수. 기본값이 정본이고, 세그먼트를 넘길 때마다 같은
   * 테이크를 다시 재지 않으려는 호출부(`useRecordMachine`)가 캐시한 것을 끼운다 —
   * 진행 저장은 세그먼트마다 일어나는데 지속음 테이크는 그 사이 바뀌지 않는다.
   */
  statsOf: (attempts: CaptureAttempts) => FrameStats = sustainStats,
): CapturedDiagnosis => {
  const filled = captureAttempts(attempts);
  const captured = framesToClips(filled);
  const took = (id: SegmentId): boolean => (attempts[id]?.length ?? 0) > 0;
  // 파생 스칼라는 지속음에서만 나온다 — 그 세그먼트를 다시 안 찍었으면 복원본 그대로.
  const stats = took('sustain') ? statsOf(filled) : (restored?.stats ?? {});
  const sustain = took('sustain')
    ? captured.sustain
    : (restored?.clips.sustain ?? captured.sustain);
  return {
    clips: {
      // 비브라토 축 점수의 재료(`Clip.vibrato`)는 **레코드에 싣는 원값 그 자체**다 — 따로 재면
      // 탭 점수와 그 아래 속도·폭이 다른 계산을 말할 수 있다. 여기서 붙이면 캐시된 `statsOf`를
      // 그대로 쓰고, 이 필드가 생기기 전에 저장된 진행을 이어도 복원본 `stats`에서 채워진다.
      sustain: { ...sustain, vibrato: stats.vibrato },
      low: took('low') ? captured.low : restored?.clips.low,
      high: took('high') ? captured.high : restored?.clips.high,
    },
    stats,
  };
};

/**
 * 이어서 한 회차의 출처.
 *
 * 재개 전후로 전송이 달라졌으면(브라우저에서 절반 찍고 앱에서 이어감) 그 회차는 native임이
 * **증명되지 않는다** — mock 클립이 섞인 채 `saveDiagnosis`를 통과하면 이력에서 어느 게
 * 실측이었는지 영영 구분할 수 없다. 모르는 쪽은 낮은 쪽으로 떨어뜨린다.
 */
export const resumedSource = (
  restored: CaptureSource | null,
  current: CaptureSource | null,
): CaptureSource =>
  current != null && (restored == null || restored === current)
    ? current
    : 'mock';
